import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  consultTokenFromMeetUrl,
  meetOriginFromUrl,
  openMeetInNewTab,
  withPortalAccessToken,
  withRoomEmbed,
} from '../../utils/videoConsultLabel';
import { apiService } from '../../services/api';
import { useMeetSession } from './MeetSessionContext';

const MEET_EMBED_SOURCE = 'aspen-meet';
const MEET_HOST_SOURCE = 'aspen-meet-host';

const IFRAME_ALLOW =
  'camera; microphone; autoplay; fullscreen; display-capture; clipboard-write';

const MINI_GAP = 12;
const DRAG_THRESHOLD = 10;
const FULL_DOCK_MQ = '(min-width: 900px)';

type MiniBox = { x: number; y: number; w: number; h: number };
type ResizeEdge = 'se' | 'sw' | 'ne' | 'nw';
type MiniGesture =
  | { kind: 'move'; dx: number; dy: number; moved: boolean; start: MiniBox }
  | { kind: 'resize'; edge: ResizeEdge; start: MiniBox; px: number; py: number };

function safeInset(side: 'top' | 'right' | 'bottom' | 'left'): number {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(`--meet-safe-${side}`);
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : 0;
}

function visibleRect(): { left: number; top: number; width: number; height: number } {
  const vv = window.visualViewport;
  const left = (vv?.offsetLeft ?? 0) + MINI_GAP + safeInset('left');
  const top = (vv?.offsetTop ?? 0) + MINI_GAP + safeInset('top');
  const width = Math.max(168, (vv?.width ?? window.innerWidth) - MINI_GAP * 2 - safeInset('left') - safeInset('right'));
  const height = Math.max(148, (vv?.height ?? window.innerHeight) - MINI_GAP * 2 - safeInset('top') - safeInset('bottom'));
  return { left, top, width, height };
}

function miniSizeLimits(rect = visibleRect()): { minW: number; minH: number; maxW: number; maxH: number } {
  const maxW = Math.min(
    rect.width,
    rect.width < 520 ? rect.width : Math.min(400, Math.round(rect.width * 0.4)),
  );
  const maxH = Math.min(
    rect.height,
    rect.height < 520 ? Math.min(rect.height, Math.round(rect.height * 0.5)) : Math.min(520, Math.round(rect.height * 0.55)),
  );
  return {
    minW: Math.min(200, maxW),
    minH: Math.min(168, maxH),
    maxW,
    maxH,
  };
}

type EmbedMessage = {
  source?: string;
  event?: string;
  consultToken?: string;
  detail?: string | null;
};

function clampMiniBox(box: MiniBox): MiniBox {
  const rect = visibleRect();
  const { minW, minH, maxW, maxH } = miniSizeLimits(rect);
  const w = Math.min(Math.max(minW, box.w), maxW);
  const h = Math.min(Math.max(minH, box.h), maxH);
  return {
    w,
    h,
    x: Math.min(Math.max(rect.left, box.x), rect.left + rect.width - w),
    y: Math.min(Math.max(rect.top, box.y), rect.top + rect.height - h),
  };
}

function defaultMiniBox(): MiniBox {
  const rect = visibleRect();
  const { maxW, maxH } = miniSizeLimits(rect);
  const w = Math.min(
    maxW,
    rect.width < 480 ? Math.min(232, rect.width) : rect.width < 900 ? 260 : 288,
  );
  const h = Math.min(
    maxH,
    rect.height < 500 ? Math.min(248, Math.round(rect.height * 0.46)) : 340,
  );
  return clampMiniBox({
    x: rect.left + rect.width - w,
    y: rect.top + rect.height - h,
    w,
    h,
  });
}

function resizeMiniBox(gesture: Extract<MiniGesture, { kind: 'resize' }>, clientX: number, clientY: number): MiniBox {
  const dx = clientX - gesture.px;
  const dy = clientY - gesture.py;
  const { start, edge } = gesture;
  let { x, y, w, h } = start;
  if (edge.includes('e'))
    w = start.w + dx;
  if (edge.includes('s'))
    h = start.h + dy;
  if (edge.includes('w')) {
    w = start.w - dx;
    x = start.x + dx;
  }
  if (edge.includes('n')) {
    h = start.h - dy;
    y = start.y + dy;
  }
  return clampMiniBox({ x, y, w, h });
}

export function VideoConsultDock() {
  const { session, mode, joined, setMode, endMeet, markJoined } = useMeetSession();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const dragRef = useRef<MiniGesture | null>(null);
  const [miniBox, setMiniBox] = useState<MiniBox | null>(null);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [slowJoin, setSlowJoin] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [patientMicOff, setPatientMicOff] = useState(false);
  const [interacting, setInteracting] = useState(false);

  const meetOrigin = session ? meetOriginFromUrl(session.joinUrl) : null;
  const expectedToken = session ? consultTokenFromMeetUrl(session.joinUrl) : null;
  const iframeSrc = session
    ? withPortalAccessToken(withRoomEmbed(session.joinUrl))
    : '';

  useEffect(() => {
    setJoinError(null);
    setSlowJoin(false);
  }, [iframeSrc, attempt]);

  const postToRoom = useCallback((command: string) => {
    const win = iframeRef.current?.contentWindow;
    if (!win || !meetOrigin)
      return;
    win.postMessage({ source: MEET_HOST_SOURCE, command }, meetOrigin);
  }, [meetOrigin]);

  useEffect(() => {
    if (!session)
      return;
    const onMessage = (e: MessageEvent<EmbedMessage>) => {
      try {
        const host = new URL(e.origin).hostname.toLowerCase();
        const allowed =
          host.endsWith('.aspenhealth.sg')
          || host === 'localhost'
          || host === '127.0.0.1'
          || host === 'aspenapi.dyndns.biz';
        if (!allowed)
          return;
      } catch {
        return;
      }
      const data = e.data;
      if (!data || data.source !== MEET_EMBED_SOURCE)
        return;
      if (expectedToken && data.consultToken && data.consultToken !== expectedToken)
        return;
      if (data.event === 'joined') {
        setJoinError(null);
        setSlowJoin(false);
        markJoined();
        postToRoom('unlock-audio');
        postToRoom('open-audio');
      }
      if (data.event === 'error')
        setJoinError(data.detail?.trim() || 'Could not join this room.');
      if (data.event === 'left' || data.event === 'ended')
        endMeet();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [session, meetOrigin, expectedToken, markJoined, endMeet, postToRoom]);

  useEffect(() => {
    if (!session)
      return;
    const onLoad = () => postToRoom('hello');
    const iframe = iframeRef.current;
    iframe?.addEventListener('load', onLoad);
    return () => iframe?.removeEventListener('load', onLoad);
  }, [session, iframeSrc, postToRoom]);

  useEffect(() => {
    if (!session)
      return;
    let cancelled = false;
    const tick = async () => {
      try {
        const status = await apiService.getTeleVisitMeetStatus(session.visitUlid);
        if (cancelled)
          return;
        setPatientMicOff(Boolean(status.patientInRoom && status.patientAudioMuted));
      } catch {
        if (!cancelled)
          setPatientMicOff(false);
      }
    };
    void tick();
    const id = window.setInterval(tick, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [session]);

  useEffect(() => {
    if (!session || joined || joinError)
      return;
    const timer = window.setTimeout(() => setSlowJoin(true), 20000);
    return () => window.clearTimeout(timer);
  }, [session, joined, joinError, iframeSrc, attempt]);

  useEffect(() => {
    if (!session || !joined)
      return;
    const unlock = () => postToRoom('unlock-audio');
    document.addEventListener('pointerdown', unlock);
    unlock();
    return () => document.removeEventListener('pointerdown', unlock);
  }, [session, joined, postToRoom]);

  const handleLeave = () => {
    postToRoom('leave');
    endMeet();
  };

  const openInTab = () => {
    if (!session)
      return;
    openMeetInNewTab(withPortalAccessToken(session.joinUrl));
  };

  useEffect(() => {
    const mq = window.matchMedia(FULL_DOCK_MQ);
    const apply = () => {
      const open = Boolean(session);
      document.body.classList.toggle('has-meet-dock', open);
      document.body.classList.toggle('has-meet-dock-full', open && mode === 'full' && mq.matches);
      document.body.classList.toggle('has-meet-dock-mini', open && mode === 'mini');
    };
    apply();
    mq.addEventListener('change', apply);
    return () => {
      mq.removeEventListener('change', apply);
      document.body.classList.remove('has-meet-dock', 'has-meet-dock-full', 'has-meet-dock-mini');
    };
  }, [session, mode]);

  const retry = () => setAttempt((n) => n + 1);

  useEffect(() => {
    if (mode !== 'mini')
      return;
    const sync = () => setMiniBox((prev) => clampMiniBox(prev ?? defaultMiniBox()));
    sync();
    const vv = window.visualViewport;
    window.addEventListener('resize', sync);
    window.addEventListener('orientationchange', sync);
    vv?.addEventListener('resize', sync);
    vv?.addEventListener('scroll', sync);
    return () => {
      window.removeEventListener('resize', sync);
      window.removeEventListener('orientationchange', sync);
      vv?.removeEventListener('resize', sync);
      vv?.removeEventListener('scroll', sync);
    };
  }, [mode]);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const gesture = dragRef.current;
      if (!gesture)
        return;
      if (gesture.kind === 'move') {
        const next = clampMiniBox({
          ...gesture.start,
          x: e.clientX - gesture.dx,
          y: e.clientY - gesture.dy,
          w: gesture.start.w,
          h: gesture.start.h,
        });
        if (Math.abs(next.x - gesture.start.x) > DRAG_THRESHOLD || Math.abs(next.y - gesture.start.y) > DRAG_THRESHOLD)
          gesture.moved = true;
        setMiniBox(next);
        return;
      }
      setMiniBox(resizeMiniBox(gesture, e.clientX, e.clientY));
    };
    const onUp = () => {
      dragRef.current = null;
      setInteracting(false);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, []);

  const onMiniPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button, .meet-dock__resize'))
      return;
    const box = e.currentTarget.closest('.meet-dock');
    if (!box)
      return;
    const rect = box.getBoundingClientRect();
    const start = miniBox ?? { x: rect.left, y: rect.top, w: rect.width, h: rect.height };
    dragRef.current = {
      kind: 'move',
      dx: e.clientX - start.x,
      dy: e.clientY - start.y,
      moved: false,
      start,
    };
    setInteracting(true);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onResizePointerDown = (edge: ResizeEdge) => (e: React.PointerEvent<HTMLSpanElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const box = e.currentTarget.closest('.meet-dock');
    if (!box)
      return;
    const rect = box.getBoundingClientRect();
    const start = miniBox ?? { x: rect.left, y: rect.top, w: rect.width, h: rect.height };
    dragRef.current = { kind: 'resize', edge, start, px: e.clientX, py: e.clientY };
    setInteracting(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  if (!session)
    return null;

  const placed = miniBox ?? (mode === 'mini' ? defaultMiniBox() : null);
  const miniStyle: React.CSSProperties | undefined = mode === 'mini' && placed
    ? {
        left: placed.x,
        top: placed.y,
        width: placed.w,
        height: placed.h,
        right: 'auto',
        bottom: 'auto',
      }
    : undefined;

  return (
    <div
      className={`meet-dock meet-dock--${mode}${interacting ? ' is-interacting' : ''}`}
      style={miniStyle}
      role="dialog"
      aria-label="Aspen Meet"
    >
      <div
        className="meet-dock__bar"
        onPointerDown={mode === 'mini' ? onMiniPointerDown : undefined}
      >
        {mode === 'mini' ? <span className="meet-dock__grip" aria-hidden="true" /> : null}
        <span className="meet-dock__title">Aspen Meet</span>
        <span
          className={`meet-dock__status${joinError ? ' is-error' : patientMicOff ? ' is-muted' : joined || (slowJoin && !joinError) ? ' is-live' : ''}`}
          title={joinError ?? undefined}
        >
          {joinError
            ? 'Could not join'
            : patientMicOff
              ? 'Mic OFF'
              : joined || slowJoin
                ? 'In call'
                : 'Joining…'}
        </span>
        <div className="meet-dock__actions">
          <button
            type="button"
            className="meet-dock__btn"
            onClick={() => postToRoom('open-audio')}
            aria-label="Audio settings"
          >
            <span className="meet-dock__btn-label">Audio</span>
          </button>
          {mode === 'full' ? (
            <button type="button" className="meet-dock__btn" onClick={openInTab}>
              <span className="meet-dock__btn-label">Open in new tab</span>
            </button>
          ) : null}
          {mode === 'full' ? (
            <button type="button" className="meet-dock__btn" onClick={() => setMode('mini')} aria-label="Minimize video">
              <MinimizeIcon />
              <span className="meet-dock__btn-label">Minimize</span>
            </button>
          ) : (
            <button type="button" className="meet-dock__btn meet-dock__btn--icon" onClick={() => setMode('full')} aria-label="Expand video">
              <ExpandIcon />
              <span className="meet-dock__btn-label">Expand</span>
            </button>
          )}
          <button
            type="button"
            className={`meet-dock__btn meet-dock__btn--leave${mode === 'mini' ? ' meet-dock__btn--icon' : ''}`}
            onClick={handleLeave}
            aria-label="Leave meeting"
          >
            {mode === 'mini' ? <CloseIcon /> : null}
            <span className="meet-dock__btn-label">Leave</span>
          </button>
        </div>
      </div>
      {patientMicOff ? (
        <p className="meet-dock__mic-alert" role="status">
          {mode === 'mini' ? 'Patient mic is OFF' : 'Patient microphone is OFF — you cannot hear them'}
        </p>
      ) : null}
      <div className="meet-dock__body">
        <iframe
          ref={iframeRef}
          key={`${session.visitUlid}:${attempt}`}
          className="meet-dock__frame"
          title="Aspen Meet"
          src={iframeSrc}
          allow={IFRAME_ALLOW}
          referrerPolicy="origin"
          allowFullScreen
        />
        {slowJoin && !joined && !joinError && mode === 'full' ? (
          <p className="meet-dock__hint">
            If the video looks blank, use Open in new tab — same room.
          </p>
        ) : null}
        {joinError && mode === 'full' ? (
          <div className="meet-dock__fallback" role="alert">
            <p className="meet-dock__fallback-title">Could not join inside the portal</p>
            <p className="meet-dock__fallback-text">{joinError}</p>
            <div className="meet-dock__fallback-actions">
              <button type="button" className="meet-dock__btn meet-dock__btn--primary" onClick={openInTab}>
                Open in new tab
              </button>
              <button type="button" className="meet-dock__btn" onClick={retry}>
                Try again here
              </button>
              <button type="button" className="meet-dock__btn meet-dock__btn--leave" onClick={handleLeave}>
                Close
              </button>
            </div>
          </div>
        ) : null}
      </div>
      {mode === 'mini' ? (
        <>
          <span className="meet-dock__resize meet-dock__resize--nw" onPointerDown={onResizePointerDown('nw')} />
          <span className="meet-dock__resize meet-dock__resize--ne" onPointerDown={onResizePointerDown('ne')} />
          <span className="meet-dock__resize meet-dock__resize--sw" onPointerDown={onResizePointerDown('sw')} />
          <span
            className="meet-dock__resize meet-dock__resize--se"
            onPointerDown={onResizePointerDown('se')}
            aria-label="Resize video"
          />
        </>
      ) : null}
    </div>
  );
}

function ExpandIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path fill="currentColor" d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
    </svg>
  );
}

function MinimizeIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path fill="currentColor" d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path fill="currentColor" d="M18.3 5.71 12 12.01l-6.3-6.3-1.4 1.41 6.29 6.29-6.3 6.3 1.42 1.4 6.29-6.29 6.3 6.3 1.4-1.41-6.29-6.3 6.3-6.29z" />
    </svg>
  );
}
